import React, { type FC, useState, useCallback, useRef, useMemo } from 'react';
import { injectIntl, type WrappedComponentProps } from 'react-intl-next';

import { Box } from '@atlaskit/primitives/compiled';

import { PRIMARY_COLORS } from '../../colors.ts';
import type { ContentState } from '../types.ts';
import type { ContentStateStateContainer } from '../ContentStateStateContainer.tsx';

import { i18n } from './contentStateMenuV2I18n.ts';
import { ContentStateMenuV2SelectView } from './ContentStateMenuV2SelectView.tsx';
import { ContentStateMenuV2CreateView } from './ContentStateMenuV2CreateView.tsx';

const IN_REVIEW_STATE = 'in-review';

enum ConstentStateMenuViewTypes {
	SELECT = 'select',
	CREATE = 'create',
}

export type ContentStateMenuV2Props = WrappedComponentProps & {
	contentId: string;
	contentStateState: ContentStateStateContainer;
	defaultStatesArray: (ContentState | null)[];
	customStatesArray: (ContentState | null)[];
	isContentStateEnabled: boolean;
	userOffline: boolean;
	onSelectExistingStatus: (selectedStatus: ContentState) => void;
	onSaveCustomStatus: (statusName: string, colorId: string) => void;
	onRemove: () => void;
	onBack: () => void;
	isContentStateMenuOpen: boolean;
	setIsContentStateMenuOpen: (newValue: boolean) => void;
};

const ContentStateMenuV2Component: FC<ContentStateMenuV2Props> = ({
	contentStateState,
	defaultStatesArray,
	customStatesArray,
	onSelectExistingStatus,
	onSaveCustomStatus,
	onRemove,
	onBack,
	setIsContentStateMenuOpen,
	intl: { formatMessage },
}) => {
	const [searchValue, setSearchValue] = useState<string>('');
	const [currentView, setCurrentView] = useState<ConstentStateMenuViewTypes>(
		ConstentStateMenuViewTypes.SELECT,
	);
	const menuRef = useRef<HTMLDivElement | null>(null);

	const validDefaultStates = useMemo(() => {
		const filtered = (defaultStatesArray || []).filter((s): s is ContentState => s !== null);

		if (!filtered.find((state) => state?.id === IN_REVIEW_STATE)) {
			const verifiedIndex = filtered.findIndex((state) => state?.restrictionLevel !== 'NONE');
			const inReviewStatus: ContentState = {
				id: IN_REVIEW_STATE,
				name: formatMessage(i18n.inReview),
				color: PRIMARY_COLORS.PURPLE,
			};
			if (verifiedIndex !== -1) {
				filtered.splice(verifiedIndex, 0, inReviewStatus);
			} else {
				filtered.push(inReviewStatus);
			}
		}

		return filtered;
	}, [defaultStatesArray, formatMessage]);

	const validCustomStates = useMemo(
		() => (customStatesArray || []).filter((s): s is ContentState => s !== null),
		[customStatesArray],
	);

	const handleSelectStatus = useCallback(
		(selectedStatus: ContentState) => {
			onSelectExistingStatus(selectedStatus);
			setIsContentStateMenuOpen(false);
			setSearchValue('');
		},
		[onSelectExistingStatus, setIsContentStateMenuOpen],
	);

	const handleRemove = useCallback(() => {
		onRemove();
		setIsContentStateMenuOpen(false);
	}, [onRemove, setIsContentStateMenuOpen]);

	const filterOptions = (options: ContentState[], query: string): ContentState[] => {
		if (!query.trim()) {
			return options;
		}
		const lowerQuery = query.toLowerCase();
		return options.filter((option) => option.name?.toLowerCase().includes(lowerQuery));
	};

	const filteredDefaultOptions = filterOptions(validDefaultStates, searchValue);
	const filteredCustomOptions = filterOptions(validCustomStates, searchValue);
	const hasResults = filteredDefaultOptions.length > 0 || filteredCustomOptions.length > 0;

	const handleAddCustomClick = useCallback(() => {
		setCurrentView(ConstentStateMenuViewTypes.CREATE);
	}, []);

	const handleBackToSearch = useCallback(() => {
		setCurrentView(ConstentStateMenuViewTypes.SELECT);
	}, []);

	const handleSaveCustomStatus = useCallback(
		(statusName: string, colorId: string) => {
			onSaveCustomStatus(statusName, colorId);
			setIsContentStateMenuOpen(false);
			setCurrentView(ConstentStateMenuViewTypes.SELECT);
			setSearchValue('');
		},
		[onSaveCustomStatus, setIsContentStateMenuOpen],
	);

	const hasCurrentStatus = contentStateState.state.contentState !== null;

	if (currentView === ConstentStateMenuViewTypes.CREATE) {
		return (
			<Box ref={menuRef}>
				<ContentStateMenuV2CreateView
					initialValue={searchValue}
					onBack={handleBackToSearch}
					onSave={handleSaveCustomStatus}
				/>
			</Box>
		);
	}

	return (
		<Box ref={menuRef} testId="content-state-menu-v2">
			<ContentStateMenuV2SelectView
				searchValue={searchValue}
				onSearchChange={setSearchValue}
				filteredDefaultOptions={filteredDefaultOptions}
				filteredCustomOptions={filteredCustomOptions}
				hasResults={hasResults}
				hasCurrentStatus={hasCurrentStatus}
				onSelectStatus={handleSelectStatus}
				onAddCustomClick={handleAddCustomClick}
				onRemove={handleRemove}
				onBack={onBack}
			/>
		</Box>
	);
};

export const ContentStateMenuV2 = injectIntl(ContentStateMenuV2Component);
